import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleById } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import ArticleComments from '../components/news/ArticleComments';

const NewsDetail = () => {
  const { id } = useParams();
  const { currentUser, hasRole, isActiveMember } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null); // Error state for future use

  // Check user permissions
  const isLoggedIn = !!currentUser;
  const isAdmin = hasRole('admin');
  const isMember = isActiveMember();

  // Mock data fallback
  const mockNewsArticles = [
    {
      id: 2,
      title: 'APN Q1 NEWSLETTER',
      image: '/images/news/q1-newsletter-full.png',
      date: 'April 11, 2022',
      author: 'Caroline Burnham',
      category: 'News',
      content: `
        <div class="news-content">
          <h2 class="text-2xl font-bold mb-4">WE ARE LIVE!</h2>
          <p class="mb-4">We are proud to announce that our website is now accessible to all prospective members of APN. Current features that we have rolled out include the membership and event registration pages, in addition to articles that detail updates about our members, general network announcements, and notifications about new events.</p>
          
          <p class="mb-6">Over the course of the coming months, APN will keep you updated via email on the rollout of new features to the site including the member directory, job posting and project posting functionality, activity forum, member group pages, and much more.</p>
          
          <p class="mb-2"><strong>SOCIAL MEDIA:</strong> APN is now active on social media. Make sure to give us a follow! You can also access our social media pages through the upper right corner of the website or through the Contact Us page.</p>
          
          <div class="flex space-x-4 my-6">
            <a href="https://www.linkedin.com/company/accelerated-professionals-network/about/?viewAsMember=true" target="_blank" rel="noopener noreferrer" class="social-link">
              <img src="/images/social/linkedin.png" alt="LinkedIn" class="w-8 h-8" />
            </a>
            <a href="https://www.instagram.com/the_apn_/" target="_blank" rel="noopener noreferrer" class="social-link">
              <img src="/images/social/instagram.png" alt="Instagram" class="w-8 h-8" />
            </a>
            <a href="https://twitter.com/the_apn_" target="_blank" rel="noopener noreferrer" class="social-link">
              <img src="/images/social/twitter.png" alt="Twitter" class="w-8 h-8" />
            </a>
          </div>
          
          <h2 class="text-2xl font-bold mb-4">Join us for our first IN-PERSON event!</h2>
          <p class="mb-6">APN is excited to be hosting our first event at Printers Alley on Tuesday, April 12th from 6:30-8:30pm. Check out the Events page to register.</p>
          
          <h2 class="text-2xl font-bold mb-4">MEMBERSHIP</h2>
          <p class="mb-6">Prospective members can now register to become an official part of the Accelerated Professionals Network. Membership will be on a tiered system, so make sure to register for the correct one! All details are on the Membership page. Feel free to contact Cara with any questions.</p>
          
          <h2 class="text-2xl font-bold mb-4">Recommended Business Books</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div class="bg-gray-100 p-4 rounded-lg">
              <h3 class="font-bold mb-2">Rich Dad Poor Dad</h3>
              <p>A classic personal finance book that teaches important lessons about money and investing.</p>
            </div>
            <div class="bg-gray-100 p-4 rounded-lg">
              <h3 class="font-bold mb-2">How to Win Friends and Influence People</h3>
              <p>Dale Carnegie's timeless advice on building relationships and effective communication.</p>
            </div>
          </div>
        </div>
      `
    }
  ];

  const loadArticle = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try Firebase first
      let articleData = await getArticleById(id);

      // If not found in Firebase, try static data
      if (!articleData) {
        console.log('Article not found in Firebase, trying static data...');
        articleData = mockNewsArticles.find(article => article.id === parseInt(id));
      }

      setArticle(articleData);
    } catch (err) {
      console.error('Error loading article:', err);
      setError(err.message);

      // Fallback to static data
      const articleData = mockNewsArticles.find(article => article.id === parseInt(id));
      setArticle(articleData);
    } finally {
      setLoading(false);
    }
  }, [id, setError]);

  // Load article from Firebase or fallback to static data
  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }
  
  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-lg text-gray-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/news" className="btn-primary">
            Back to News
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">{article.title}</h1>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded mr-2">
                  {article.category}
                </span>
                <span className="text-gray-500 text-sm">
                  {article.publishedAt?.toDate ?
                    article.publishedAt.toDate().toLocaleDateString() :
                    article.date
                  }
                </span>
              </div>
              <div className="text-gray-500 text-sm">
                By {article.author}
              </div>
            </div>
            
            {/* Featured Image */}
            <div className="mb-8">
              <img 
                src={`${process.env.PUBLIC_URL}${article.image}`} 
                alt={article.title}
                className="w-full h-auto rounded-lg shadow-md"
              />
            </div>
            
            {/* Article Content */}
            <div 
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            
            {/* Social Share */}
            <div className="mt-12 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Share This Article</h3>
              <div className="flex space-x-4">
                <a 
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-apn-blue transition-colors"
                >
                  <img src="/images/social/linkedin.png" alt="LinkedIn" className="w-6 h-6" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-apn-blue transition-colors"
                >
                  <img src="/images/social/twitter.png" alt="Twitter" className="w-6 h-6" />
                </a>
                <a 
                  href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(window.location.href)}`} 
                  className="text-gray-500 hover:text-apn-blue transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Article Comments and Reactions */}
          <ArticleComments
            article={article}
            isLoggedIn={isLoggedIn}
            isAdmin={isAdmin}
            isMember={isMember}
          />

          <div className="mt-8 text-center">
            <Link to="/news" className="btn-primary">
              Back to News
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;
